import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import {
  PlatformChatRankingCriteria,
  type LacTenantOverviewDateRange,
  type PlatformChatRankingDTO,
} from '../../types/lac';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { useLacTopPlatformChatsQuery } from '../../features/lac/queries/useLacTopPlatformChatsQuery';
import { sumAnalyticsRoleCounts } from '../../features/lac/sumAnalyticsRoleCounts';
import { useI18n } from '../../i18n/useI18n';
import { formatNumber } from '../../utils/formatUtils';
import { LacStatsTopSectionTitle } from './LacStatsTopSectionTitle';
import './LacTenantStatsKbCard.css';

const PLATFORM_CHAT_CRITERIA_OPTIONS = [
  PlatformChatRankingCriteria.Messages,
  PlatformChatRankingCriteria.Participants,
  PlatformChatRankingCriteria.KnowledgeBases,
] as const;

type LacTenantStatsPlatformChatsSectionProps = {
  tenantName: string;
  knowledgeBaseId: string | null;
  dateRange?: LacTenantOverviewDateRange;
};

export function LacTenantStatsPlatformChatsSection({
  tenantName,
  knowledgeBaseId,
  dateRange,
}: LacTenantStatsPlatformChatsSectionProps) {
  const { locale, translate } = useI18n();
  const [criteria, setCriteria] = useState<PlatformChatRankingCriteria>(
    PlatformChatRankingCriteria.Messages,
  );

  const topQuery = useLacTopPlatformChatsQuery(
    tenantName,
    criteria,
    knowledgeBaseId,
    dateRange,
  );

  const rows = topQuery.data ?? [];
  const isLoading = topQuery.isLoading;
  const isError = topQuery.isError;

  const criteriaOptions = useMemo(
    () =>
      PLATFORM_CHAT_CRITERIA_OPTIONS.map((value) => ({
        value,
        label: translate(
          `lac.organisations.stats.platformChats.criteria.${value}`,
        ),
      })),
    [translate],
  );

  return (
    <section
      className="lac-tenant-stats-section"
      aria-label={translate('lac.organisations.stats.platformChats.legend')}
    >
      <div className="lac-tenant-stats-section__header">
        <LacStatsTopSectionTitle>
          {translate('lac.organisations.stats.platformChats.title')}
        </LacStatsTopSectionTitle>
        <div className="lac-tenant-stats-section__criteria">
          <ArrowUpDown
            className="lac-tenant-stats-section__criteria-icon"
            strokeWidth={2}
            aria-hidden
          />
          <LiksDropdown
            className="lac-tenant-stats-section__criteria-dropdown"
            variant="on-light"
            value={criteria}
            onChange={(value) =>
              setCriteria(value as PlatformChatRankingCriteria)
            }
            options={criteriaOptions}
            aria-label={translate(
              'lac.organisations.stats.platformChats.criteriaLabel',
            )}
          />
        </div>
      </div>

      {isLoading ? (
        <p className="lac-tenant-overview__status">
          {translate('lac.organisations.stats.platformChats.loading')}
        </p>
      ) : null}

      {isError ? (
        <p className="lac-tenant-overview__status" role="alert">
          {translate('lac.organisations.stats.platformChats.loadError')}
        </p>
      ) : null}

      {!isLoading && !isError && rows.length === 0 ? (
        <p className="lac-tenant-overview__status">
          {translate('lac.organisations.stats.platformChats.empty')}
        </p>
      ) : null}

      {!isLoading && !isError && rows.length > 0 ? (
        <ul className="lac-tenant-stats-kb__grid">
          {rows.map((row, index) => (
            <li key={row.sessionId ?? `platform-chat-${index}`}>
              <PlatformChatRankingCard row={row} locale={locale} />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PlatformChatRankingCard({
  row,
  locale,
}: {
  row: PlatformChatRankingDTO;
  locale: string;
}) {
  const { translate } = useI18n();
  const title =
    row.sessionTitle?.trim() ||
    row.sessionId?.trim() ||
    translate('lac.organisations.stats.platformChats.untitled');
  const linkedNames = (row.linkedKnowledgeBases ?? [])
    .map(
      (item) => item.knowledgeBaseName?.trim() || item.knowledgeBaseId?.trim(),
    )
    .filter((name): name is string => Boolean(name));
  const description =
    linkedNames.length > 0
      ? linkedNames.join(', ')
      : translate('lac.organisations.stats.platformChats.noKnowledgeBase');
  const participantCount = sumAnalyticsRoleCounts(row.participantRoleCounts);

  return (
    <div className="lac-tenant-stats-kb__card">
      <div className="lac-tenant-stats-kb__card-main lac-tenant-stats-kb__card-main--static">
        <span className="lac-tenant-stats-kb__card-title">{title}</span>
        <span
          className={[
            'lac-tenant-stats-kb__card-description',
            linkedNames.length === 0 &&
              'lac-tenant-stats-kb__card-description--empty',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {description}
        </span>
        <dl className="lac-tenant-stats-kb__card-stats">
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate('lac.organisations.stats.platformChats.participants')}
            </dt>
            <dd>{formatNumber(participantCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate('lac.organisations.stats.platformChats.messages')}
            </dt>
            <dd>{formatNumber(row.messageCount, locale)}</dd>
          </div>
          <div className="lac-tenant-stats-kb__card-stat">
            <dt>
              {translate(
                'lac.organisations.stats.platformChats.knowledgeBases',
              )}
            </dt>
            <dd>{formatNumber(row.knowledgeBaseLinkCount, locale)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
